import React, { ReactNode, useState } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { ArrowLeft, ArrowRight, CheckCircle } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { api } from '../../../services/api';
import { urlDoMcp } from '../mcpUtils';
import { Aviso, BlocoMono, BotaoPrimario, BotaoSecundario, Cartao, Eyebrow } from '../ui';

type Secao = 'visao' | 'ferramentas' | 'acesso' | 'atividade';

interface Passo {
  titulo: string;
  resumo: string;
  itens: string[];
  codigo?: string;
  aviso?: string;
  acao?: { rotulo: string; secao?: Secao; rota?: string };
}

function passosDoGuia(urlMcp: string, urlLogin: string): Passo[] {
  return [
    {
      titulo: 'Conferir se o MCP está ligado',
      resumo: 'Antes de tudo, veja o estado.',
      itens: [
        'Abra a aba Resumo.',
        'No topo deve aparecer "MCP ligado". Se aparecer "desligado", quem cuida do servidor precisa ativá-lo (variável MCP_ATIVO). Não dá para ligar por aqui.',
        'Escrita é outra chave (MCP_ESCRITA_ATIVA). Sem ela, a IA só consulta e propõe: não grava nada.',
      ],
      acao: { rotulo: 'Abrir o Resumo', secao: 'visao' },
    },
    {
      titulo: 'Liberar o IP da máquina',
      resumo: 'Só IP liberado consegue falar com o MCP.',
      itens: [
        'Descubra o IP de saída de quem vai usar a IA (a sua rede, não o IP interno).',
        'Vá em Configurações > IP whitelist e adicione esse IP.',
        'Volte ao Resumo: o card "Acesso por IP" mostra se o seu IP atual já está liberado.',
      ],
      aviso: 'Trocou de rede ou de VPN? O IP muda e é preciso liberar de novo.',
      acao: { rotulo: 'Abrir IP whitelist', rota: '/configuracoes/ip-whitelist' },
    },
    {
      titulo: 'Criar a chave da rede',
      resumo: 'É o primeiro login. Dá acesso só às ferramentas gerais.',
      itens: [
        'Abra a aba Acesso e clique em "Nova chave".',
        'Dê um nome que diga quem usa (ex.: "Notebook do TI").',
        'Copie a chave que aparece (começa com mcpk_). Ela só é mostrada nessa hora. Se perder, gere outra.',
        'Guarde num cofre de senhas. Não coloque em chat, e-mail ou código.',
      ],
      aviso:
        'Se a chave vazar, use "Girar chave" ou desative a chave na mesma tela. As sessões abertas com ela caem na hora.',
      acao: { rotulo: 'Abrir Acesso', secao: 'acesso' },
    },
    {
      titulo: 'Criar a senha do MCP do usuário',
      resumo: 'É o segundo login. Define o que a IA pode fazer em nome da pessoa.',
      itens: [
        'Vá na Central de Permissões > Usuários e abra o usuário.',
        'Role até o campo MCP.',
        'Digite uma senha com 12 caracteres ou mais, ou clique em "Gerar senha" para o sistema criar uma.',
        'Copie na hora. Ela não volta a aparecer.',
        'Confira se o selo ao lado mostra "acesso ativo".',
        'Pronto: a IA, autenticada com essa senha, só usa o que esse usuário já pode no sistema. Administrador usa tudo o que estiver habilitado.',
      ],
      aviso:
        'Essa senha vale só no MCP. Não é a senha do sistema. Para encerrar o acesso, use "Remover senha" no mesmo campo.',
      acao: { rotulo: 'Abrir a Central de Permissões', rota: '/configuracoes/permissoes' },
    },
    {
      titulo: 'Conectar a IA ao MCP',
      resumo: 'Troque a chave por um token de 24 horas.',
      itens: [
        'Envie a chave para o endereço de login abaixo.',
        'A resposta traz um token (começa com mcpt_). Ele vale 24 horas e só funciona no IP que fez o login.',
        'No cliente da IA, cadastre o servidor MCP com este endereço e o cabeçalho Authorization: Bearer <token>.',
      ],
      codigo: `POST ${urlLogin}\n{"chave": "mcpk_..."}\n\nEndereço do MCP:\n${urlMcp}\nAuthorization: Bearer mcpt_...`,
      aviso: 'Passou das 24 horas? Faça o login de novo para receber outro token.',
    },
    {
      titulo: 'Entrar como usuário',
      resumo: 'Só com isso a IA ganha as ferramentas da pessoa.',
      itens: [
        'Peça para a IA chamar a ferramenta mcp_autenticar_usuario com o e-mail do usuário e a senha do MCP do passo 4.',
        'Depois do login, a lista de ferramentas muda conforme as permissões dele.',
        'Errou a senha várias vezes? O acesso trava por alguns minutos.',
        'Para sair, use mcp_encerrar_usuario.',
      ],
      codigo:
        'tools/call → mcp_autenticar_usuario\n{"email": "pessoa@empresa.com.br", "senha": "..."}',
      aviso:
        'A senha passa pela conversa com a IA. Use uma senha só do MCP e troque quando precisar.',
    },
    {
      titulo: 'Escolher o que cada grupo pode usar',
      resumo: 'Opcional. Por padrão vale a permissão que a pessoa já tem.',
      itens: [
        'Na aba Ferramentas, abra uma ferramenta.',
        'Em "Quem pode usar", marque grupos ou pessoas. Quem não estiver na lista não a vê.',
        'Em "Como a IA usa", ajuste o texto que a IA lê, o limite por minuto e a confirmação.',
        'As ferramentas que alteram permissões ficam só com administrador até você marcar alguém.',
      ],
      acao: { rotulo: 'Abrir Ferramentas', secao: 'ferramentas' },
    },
    {
      titulo: 'Acompanhar o que foi feito',
      resumo: 'Toda chamada fica registrada.',
      itens: [
        'Na aba Atividade você vê quem chamou, qual ferramenta, de qual IP e o resultado.',
        'Em Acesso > Sessões, revogue qualquer sessão que pareça estranha.',
        'A IA também consegue listar o que fez com a ferramenta mcp_minhas_acoes.',
      ],
      acao: { rotulo: 'Abrir Atividade', secao: 'atividade' },
    },
  ];
}

function Numeracao({ n }: { n: number }) {
  return (
    <Flex
      w="22px"
      h="22px"
      borderRadius="50%"
      bg="#DFF3FF"
      color="#075985"
      align="center"
      justify="center"
      fontSize="11.5px"
      fontWeight={700}
      flex="none"
      mt="1px"
    >
      {n}
    </Flex>
  );
}

function Trilha({
  passos,
  atual,
  feitos,
  onEscolher,
}: {
  passos: Passo[];
  atual: number;
  feitos: Set<number>;
  onEscolher: (i: number) => void;
}) {
  return (
    <Flex direction="column" gap="4px">
      {passos.map((p, i) => {
        const ativo = i === atual;
        let icone: ReactNode = (
          <Text fontSize="11.5px" fontWeight={700}>
            {i + 1}
          </Text>
        );
        if (feitos.has(i)) icone = <CheckCircle size={18} weight="fill" color="#15803D" />;
        return (
          <Flex
            key={p.titulo}
            as="button"
            type="button"
            onClick={() => onEscolher(i)}
            align="center"
            gap="10px"
            p="8px 10px"
            borderRadius="10px"
            textAlign="left"
            bg={ativo ? '#EAF2FF' : 'transparent'}
            color={ativo ? '#1D4ED8' : '#334155'}
            fontWeight={ativo ? 700 : 500}
            fontSize="13px"
            _hover={{ bg: ativo ? '#EAF2FF' : '#F1F5F9' }}
          >
            <Flex w="20px" justify="center" flex="none">
              {icone}
            </Flex>
            <Text>{p.titulo}</Text>
          </Flex>
        );
      })}
    </Flex>
  );
}

export function GuiaTab({ irPara }: { irPara: (secao: Secao) => void }) {
  const navigate = useNavigate();
  const urlMcp = urlDoMcp(api.defaults.baseURL, '/mcp/');
  const urlLogin = `${urlMcp.replace(/\/+$/, '')}/login/`;
  const passos = passosDoGuia(urlMcp, urlLogin);
  const [atual, setAtual] = useState(0);
  const [feitos, setFeitos] = useState<Set<number>>(new Set());

  const passo = passos[atual];
  const ultimo = atual === passos.length - 1;

  const concluirEAvancar = () => {
    setFeitos(f => new Set(f).add(atual));
    if (!ultimo) setAtual(atual + 1);
  };

  const abrir = () => {
    if (passo.acao?.secao) irPara(passo.acao.secao);
    else if (passo.acao?.rota) navigate(passo.acao.rota);
  };

  return (
    <Flex gap="16px" align="flex-start" direction={{ base: 'column', lg: 'row' }}>
      <Cartao p="14px 12px" w={{ base: '100%', lg: '280px' }} flex="none">
        <Box px="10px" pb="8px">
          <Eyebrow>PASSO A PASSO</Eyebrow>
          <Text fontSize="12px" color="#64748B" mt="2px">
            {feitos.size} de {passos.length} concluídos
          </Text>
        </Box>
        <Trilha passos={passos} atual={atual} feitos={feitos} onEscolher={setAtual} />
      </Cartao>

      <Cartao
        p="22px 24px"
        flex="1 1 auto"
        minW={0}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <Box>
          <Eyebrow>
            PASSO {atual + 1} DE {passos.length}
          </Eyebrow>
          <Text fontSize="20px" lineHeight="26px" fontWeight={700} color="#0F172A">
            {passo.titulo}
          </Text>
          <Text fontSize="14px" color="#475569" mt="2px">
            {passo.resumo}
          </Text>
        </Box>

        <Flex direction="column" gap="10px">
          {passo.itens.map((t, i) => (
            <Flex key={t} gap="10px" align="flex-start">
              <Numeracao n={i + 1} />
              <Text fontSize="14px" lineHeight="21px" color="#1E293B">
                {t}
              </Text>
            </Flex>
          ))}
        </Flex>

        {passo.codigo && <BlocoMono copiar>{passo.codigo}</BlocoMono>}
        {passo.aviso && <Aviso tom="aviso">{passo.aviso}</Aviso>}

        <Flex gap="8px" wrap="wrap" align="center" pt="4px">
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<ArrowLeft size={14} />}
            isDisabled={atual === 0}
            onClick={() => setAtual(atual - 1)}
          >
            Voltar
          </Button>
          <Box flex={1} />
          {passo.acao && <BotaoSecundario onClick={abrir}>{passo.acao.rotulo}</BotaoSecundario>}
          <BotaoPrimario onClick={concluirEAvancar}>
            {ultimo ? 'Concluir' : 'Feito, próximo'}
            {!ultimo && <ArrowRight size={14} style={{ marginLeft: 6 }} />}
          </BotaoPrimario>
        </Flex>
      </Cartao>
    </Flex>
  );
}
